<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import InstagramAutomationAPI from 'dashboard/api/instagramAutomation';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  keywordsFromInput,
  newNodeItemId,
  URL_BUTTON_TITLE_MAX,
} from './workflowInstagramNodeConfig';

/**
 * Assistente «Comentário para DM»: uma tela, um fluxo pronto. Gera um Chat
 * Flow com o gatilho «Comentário no Instagram» (post, palavras e a resposta
 * no direct com botão), a resposta pública e, se escolhida, a etiqueta. Quem
 * quiser mais abre o fluxo no editor e continua de lá.
 */

const emit = defineEmits(['created']);

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const labelsRaw = useMapGetter('labels/getLabels');

const dialog = ref(null);
const isSaving = ref(false);
const media = ref([]);
const mediaLoading = ref(false);
const mediaError = ref(false);
// Sem Instagram conectado o fluxo seria criado e nunca dispararia: o
// assistente confere a conexão antes e, sem ela, leva para Integrações.
// 'checking' | 'connected' | 'missing' | 'unknown' (a checagem falhou)
const connectionState = ref('checking');

const form = ref({
  name: '',
  mediaId: '',
  keywords: '',
  dmText: '',
  buttonTitle: '',
  link: '',
  publicReply: '',
  labelId: '',
});

const labelsSorted = computed(() =>
  [...(labelsRaw.value || [])].sort((a, b) =>
    String(a.title).localeCompare(String(b.title))
  )
);

const mediaLabel = item => {
  const caption = String(item?.caption || '')
    .replace(/\s+/g, ' ')
    .trim();
  const snippet = caption.length > 56 ? `${caption.slice(0, 56)}…` : caption;
  const date = item?.timestamp
    ? new Date(item.timestamp).toLocaleDateString('pt-BR')
    : '';
  const text = snippet || t('FLOW_AUTOMATIONS.WIZARD.NO_CAPTION');
  return date ? `${date} · ${text}` : text;
};

const loadMedia = async () => {
  mediaLoading.value = true;
  mediaError.value = false;
  try {
    const { data } = await InstagramAutomationAPI.media();
    media.value = Array.isArray(data?.data) ? data.data : [];
  } catch {
    media.value = [];
    mediaError.value = true;
  } finally {
    mediaLoading.value = false;
  }
};

const checkConnection = async () => {
  connectionState.value = 'checking';
  try {
    const { data } = await InstagramAutomationAPI.getConnection();
    connectionState.value =
      data?.configured === true ? 'connected' : 'missing';
  } catch {
    // Falha de rede não prova que falta conexão: deixa seguir com aviso.
    connectionState.value = 'unknown';
  }
  if (connectionState.value !== 'missing' && !media.value.length) {
    loadMedia();
  }
};

const isMissingConnection = computed(
  () => connectionState.value === 'missing'
);

const canSubmit = computed(
  () =>
    form.value.dmText.trim().length > 0 &&
    !isSaving.value &&
    connectionState.value !== 'checking' &&
    !isMissingConnection.value
);

const goToConnection = () => {
  dialog.value?.close();
  router.push({ name: 'settings_integrations_meta' });
};

const open = () => {
  form.value = {
    name: t('FLOW_AUTOMATIONS.WIZARD.DEFAULT_NAME'),
    mediaId: '',
    keywords: '',
    // A variável do fluxo entra por parâmetro: chaves duplas na mensagem
    // i18n seriam lidas como placeholder do vue-i18n.
    dmText: t('FLOW_AUTOMATIONS.WIZARD.DEFAULT_DM', {
      name: '{{contact.name}}',
    }),
    buttonTitle: '',
    link: '',
    publicReply: t('FLOW_AUTOMATIONS.WIZARD.DEFAULT_PUBLIC_REPLY'),
    labelId: '',
  };
  dialog.value?.open();
  checkConnection();
};

defineExpose({ open });

watch(
  () => form.value.buttonTitle,
  value => {
    if (value.length > URL_BUTTON_TITLE_MAX) {
      form.value.buttonTitle = value.slice(0, URL_BUTTON_TITLE_MAX);
    }
  }
);

onMounted(() => {
  store.dispatch('labels/get');
});

// Gatilho à esquerda, resposta pública e etiqueta em sequência, como a pessoa
// veria se montasse à mão no editor.
const buildGraph = () => {
  const values = form.value;
  const nodes = [];
  const edges = [];
  const triggerId = newNodeItemId();
  nodes.push({
    id: triggerId,
    type: 'trigger',
    position: { x: 80, y: 160 },
    data: {
      kind: 'instagram_comment',
      label: t('FLOW_AUTOMATIONS.WIZARD.TRIGGER_LABEL'),
      comment_filters: {
        media_id: values.mediaId,
        keywords: keywordsFromInput(values.keywords),
        exclude_keywords: [],
        top_level_only: true,
        live_comments: false,
      },
      first_reply: {
        text: values.dmText.trim(),
        button_title: values.buttonTitle.trim(),
        link: values.link.trim(),
      },
    },
  });
  let previous = triggerId;
  let x = 420;
  if (values.publicReply.trim()) {
    const id = newNodeItemId();
    nodes.push({
      id,
      type: 'action',
      position: { x, y: 160 },
      data: {
        kind: 'instagram_public_reply',
        label: t('FLOW_AUTOMATIONS.WIZARD.PUBLIC_REPLY_LABEL'),
        public_reply_texts: [values.publicReply.trim()],
      },
    });
    edges.push({ id: `${previous}-${id}`, source: previous, target: id });
    previous = id;
    x += 340;
  }
  if (values.labelId) {
    const id = newNodeItemId();
    nodes.push({
      id,
      type: 'action',
      position: { x, y: 160 },
      data: {
        kind: 'add_label',
        label: t('FLOW_AUTOMATIONS.WIZARD.LABEL_LABEL'),
        label_id: Number(values.labelId),
      },
    });
    edges.push({ id: `${previous}-${id}`, source: previous, target: id });
  }
  return { nodes, edges };
};

const submit = async () => {
  if (!canSubmit.value) return;
  isSaving.value = true;
  try {
    const { data } = await conversationWorkflowsAPI.create({
      conversation_workflow: {
        name:
          form.value.name.trim() || t('FLOW_AUTOMATIONS.WIZARD.DEFAULT_NAME'),
        description: t('FLOW_AUTOMATIONS.WIZARD.DESCRIPTION_SAVED'),
        folder: 'Instagram',
        active: true,
        graph: buildGraph(),
      },
    });
    useAlert(t('FLOW_AUTOMATIONS.WIZARD.CREATED'));
    dialog.value?.close();
    emit('created', data?.payload?.id);
  } catch {
    useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialog"
    width="lg"
    :title="t('FLOW_AUTOMATIONS.WIZARD.TITLE')"
    :description="t('FLOW_AUTOMATIONS.WIZARD.DESCRIPTION')"
    :confirm-button-label="t('FLOW_AUTOMATIONS.WIZARD.CONFIRM')"
    :cancel-button-label="t('FLOW_AUTOMATIONS.WIZARD.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :show-confirm-button="!isMissingConnection"
    :is-loading="isSaving"
    @confirm="submit"
  >
    <div
      v-if="isMissingConnection"
      class="flex flex-col items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-6 py-8 text-center"
      data-testid="comment-to-dm-no-connection"
    >
      <span
        class="i-ri-instagram-line size-8 text-n-slate-10"
        aria-hidden="true"
      />
      <p class="m-0 text-sm font-medium text-n-slate-12">
        {{ t('FLOW_AUTOMATIONS.WIZARD.NO_CONNECTION_TITLE') }}
      </p>
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('FLOW_AUTOMATIONS.WIZARD.NO_CONNECTION_DESCRIPTION') }}
      </p>
      <Button
        type="button"
        :label="t('FLOW_AUTOMATIONS.WIZARD.CONNECT')"
        icon="i-ri-instagram-line"
        @click="goToConnection"
      />
    </div>
    <div v-else class="flex flex-col gap-4" data-testid="comment-to-dm-wizard">
      <p
        v-if="connectionState === 'unknown'"
        role="status"
        class="m-0 rounded-xl border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-sm text-n-amber-11"
      >
        {{ t('FLOW_AUTOMATIONS.WIZARD.CONNECTION_CHECK_FAILED') }}
      </p>
      <label class="flex flex-col gap-1.5">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('FLOW_AUTOMATIONS.WIZARD.NAME') }}
        </span>
        <input
          v-model="form.name"
          type="text"
          class="reset-base box-border h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        />
      </label>

      <label class="flex flex-col gap-1.5">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('FLOW_AUTOMATIONS.WIZARD.MEDIA') }}
        </span>
        <select
          v-model="form.mediaId"
          class="reset-base box-border h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        >
          <option value="">{{ t('FLOW_AUTOMATIONS.WIZARD.ANY_MEDIA') }}</option>
          <option v-for="item in media" :key="item.id" :value="String(item.id)">
            {{ mediaLabel(item) }}
          </option>
        </select>
        <span v-if="mediaLoading" class="text-xs text-n-slate-11">
          {{ t('FLOW_AUTOMATIONS.WIZARD.MEDIA_LOADING') }}
        </span>
        <span v-else-if="mediaError" class="text-xs text-n-ruby-11">
          {{ t('FLOW_AUTOMATIONS.WIZARD.MEDIA_ERROR') }}
        </span>
      </label>

      <label class="flex flex-col gap-1.5">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('FLOW_AUTOMATIONS.WIZARD.KEYWORDS') }}
        </span>
        <input
          v-model="form.keywords"
          type="text"
          :placeholder="t('FLOW_AUTOMATIONS.WIZARD.KEYWORDS_PLACEHOLDER')"
          class="reset-base box-border h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        />
        <span class="text-xs leading-snug text-n-slate-11">
          {{ t('FLOW_AUTOMATIONS.WIZARD.KEYWORDS_HINT') }}
        </span>
      </label>

      <label class="flex flex-col gap-1.5">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('FLOW_AUTOMATIONS.WIZARD.DM_TEXT') }}
        </span>
        <textarea
          v-model="form.dmText"
          rows="4"
          class="reset-base box-border min-h-[5rem] w-full resize-y rounded-xl border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        />
      </label>

      <div
        class="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]"
      >
        <label class="flex flex-col gap-1.5">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('FLOW_AUTOMATIONS.WIZARD.BUTTON_TITLE') }}
          </span>
          <input
            v-model="form.buttonTitle"
            type="text"
            :maxlength="URL_BUTTON_TITLE_MAX"
            class="reset-base box-border h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          />
        </label>
        <label class="flex flex-col gap-1.5">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('FLOW_AUTOMATIONS.WIZARD.LINK') }}
          </span>
          <input
            v-model="form.link"
            type="url"
            :placeholder="t('FLOW_AUTOMATIONS.WIZARD.LINK_PLACEHOLDER')"
            class="reset-base box-border h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          />
        </label>
      </div>
      <p class="text-xs leading-snug text-n-slate-11">
        {{ t('FLOW_AUTOMATIONS.WIZARD.LINK_HINT') }}
      </p>

      <label class="flex flex-col gap-1.5">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('FLOW_AUTOMATIONS.WIZARD.PUBLIC_REPLY') }}
        </span>
        <input
          v-model="form.publicReply"
          type="text"
          class="reset-base box-border h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        />
      </label>

      <label class="flex flex-col gap-1.5">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('FLOW_AUTOMATIONS.WIZARD.LABEL') }}
        </span>
        <select
          v-model="form.labelId"
          class="reset-base box-border h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        >
          <option value="">{{ t('FLOW_AUTOMATIONS.WIZARD.NO_LABEL') }}</option>
          <option
            v-for="lb in labelsSorted"
            :key="lb.id"
            :value="String(lb.id)"
          >
            {{ lb.title }}
          </option>
        </select>
      </label>
    </div>
  </Dialog>
</template>
